import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBestPerformers } from "../../../redux/action/analytics";
import { useMediaQuery } from "react-responsive";
import { ProgressBar } from "react-materialize";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { startOfYear, format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { IWMURL } from "../../../redux/config/urlConfig";

function BestPerformers() {
    const dispatch = useDispatch();
    const { bestPerformers, bestPerformersLoading } = useSelector(
        (state) => state.analytics
    );
    const { organization } = useSelector((state) => state.organization);
    const isTablet = useMediaQuery({ query: "(max-width: 1224px)" });
    const pdfExportRef = useRef(null);

    // Set default start date to beginning of year and end date to today
    const [startDate, setStartDate] = useState(startOfYear(new Date()));
    const [endDate, setEndDate] = useState(new Date());

    const onChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
    };

    useEffect(() => {
        if (!startDate || !endDate) return;
        // Format dates as ISO strings for the API
        const start = startDate ? startDate.toISOString().split('T')[0] : null;
        const end = endDate ? endDate.toISOString().split('T')[0] : null;
        dispatch(getBestPerformers(start, end));
    }, [startDate, endDate, dispatch]);

    const handleExportPDF = async () => {
        if (!pdfExportRef.current) return;

        try {
            // Capture the table content
            const canvas = await html2canvas(pdfExportRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            // Create PDF
            const pdf = new jsPDF('portrait', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pdfWidth - 40; // margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Header with purple background
            pdf.setFillColor(111, 45, 168); // #6f2da8
            pdf.rect(0, 0, pdfWidth, 40, 'F');

            // Try to add logo
            if (organization?._id) {
                try {
                    const logoUrl = `${IWMURL}/organization/read/logo/${organization._id}`;
                    const logoImg = await new Promise((resolve, reject) => {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.onload = () => resolve(img);
                        img.onerror = reject;
                        img.src = logoUrl;
                    });

                    const logoCanvas = document.createElement('canvas');
                    logoCanvas.width = logoImg.width;
                    logoCanvas.height = logoImg.height;
                    const logoCtx = logoCanvas.getContext('2d');
                    logoCtx.drawImage(logoImg, 0, 0);
                    const logoData = logoCanvas.toDataURL('image/png');
                    pdf.addImage(logoData, 'PNG', 15, 10, 20, 20);
                } catch (error) {
                    console.log('Logo not available, continuing without it');
                }
            }

            // Company name
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(18);
            pdf.setFont('helvetica', 'bold');
            pdf.text(organization?.name || 'Company Name', 40, 20);

            // Report title
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');
            pdf.text('Best Performers Report', 40, 30);

            // Date range
            pdf.setFontSize(10);
            const dateRange = `${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`;
            pdf.text(dateRange, 40, 36);

            // Add table content
            const contentY = 50;
            let yPosition = contentY;
            let remainingHeight = imgHeight;
            const pageHeight = pdfHeight - contentY - 20; // Leave space for footer

            if (remainingHeight <= pageHeight) {
                // Content fits on one page
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 20, yPosition, imgWidth, imgHeight);
            } else {
                // Content spans multiple pages
                let sourceY = 0;
                while (remainingHeight > 0) {
                    const chunkHeight = Math.min(remainingHeight, pageHeight);
                    const sourceHeight = (chunkHeight / imgHeight) * canvas.height;

                    const chunkCanvas = document.createElement('canvas');
                    chunkCanvas.width = canvas.width;
                    chunkCanvas.height = sourceHeight;
                    const chunkCtx = chunkCanvas.getContext('2d');
                    chunkCtx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);

                    pdf.addImage(chunkCanvas.toDataURL('image/png'), 'PNG', 20, yPosition, imgWidth, chunkHeight);

                    remainingHeight -= chunkHeight;
                    sourceY += sourceHeight;

                    if (remainingHeight > 0) {
                        pdf.addPage();
                        yPosition = 20;
                    }
                }
            }

            // Footer on last page
            pdf.setFontSize(8);
            pdf.setTextColor(102, 102, 102);
            pdf.text(
                `Generated on ${format(new Date(), 'dd MMMM yyyy, hh:mm a')}`,
                pdfWidth / 2,
                pdfHeight - 10,
                { align: 'center' }
            );

            // Save PDF
            pdf.save(`Best_Performers_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    if (bestPerformersLoading) {
        return (
            <div className="box full-width white">
                <div className="flex ph-1 align-center space-between">
                    <b className="bold">Best Performers</b>
                </div>
                <div className="flex justify-center align-center p-2">
                    <ProgressBar />
                </div>
            </div>
        );
    }

    return (
        <div className="box full-width white">
            <div className="flex ph-1 align-center space-between">
                <b className="bold">Best Performers from My Network </b>
                <div className="flex align-center gap-1">
                    <button
                        className="btn-small cercle-purple white-text"
                        onClick={handleExportPDF}
                        disabled={!bestPerformers || bestPerformers.length === 0}
                        style={{ marginRight: '10px' }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                            picture_as_pdf
                        </span>
                        Export PDF
                    </button>
                    <div className="flex hover">
                        <div className="flex align-center gap-1 chart-date-picker">
                            <DatePicker
                                selected={startDate}
                                onChange={onChange}
                                startDate={startDate}
                                endDate={endDate}
                                selectsRange
                                className="browser-default"
                                dateFormat="dd MMMM yyyy"
                            />
                            <span className="material-symbols-outlined">calendar_month</span>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={pdfExportRef} style={{ padding: '20px' }}>

                <table
                    className={
                        isTablet
                            ? "striped full-width responsive-table"
                            : "striped full-width responsive-table accounttable"
                    }
                >
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Organization</th>
                            <th>Total Quantity Generated</th>
                            <th>Total Quantity Accepted</th>
                            <th>Total Completed</th>
                        </tr>
                    </thead>

                    <tbody>
                        {bestPerformers?.map((item, index) => {
                            return (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.organization?.name || "N/A"}</td>
                                    <td>
                                        {item.totalQuantityGenerated?.toFixed(2) || 0}{" "}
                                        {item.unit || ""}
                                    </td>
                                    <td>
                                        {item.totalQuantityAccepted?.toFixed(2) || 0}{" "}
                                        {item.unit || ""}
                                    </td>
                                    <td>
                                        {item.totalCompleted?.toFixed(2) || 0} {item.unit || ""}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {(!bestPerformers || bestPerformers.length === 0) && (
                    <div className="flex column align-center full-width mv-2 p-2">
                        <span
                            class="material-symbols-outlined grey-text"
                            style={{ fontSize: "5rem" }}
                        >
                            trending_up
                        </span>
                        <p className="grey-text mt-1">No performance data available.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BestPerformers;
